import React, { useContext, useEffect, useState } from 'react';

import { Drawer, PrimaryButton, SecondaryButton, Switch } from '@zooplus/ui-components';

import CartEventsContext from '../../../common/contexts/CartEventsContext';
import LocResourcesContext from '../../../common/contexts/LocResourcesContext';
import { View } from '../../../common/utils/types';
import FiltersContext from '../../contexts/FiltersContext';

import { FilterCategoryButton } from './FilterCategoryButton';
import { ListSelector } from './ListSelector';
import { ZooPointsSlider } from './ZooPointsSlider';

type FiltersDrawerProps = {
  view: View;
};

export const FiltersDrawer = ({ view }: FiltersDrawerProps): JSX.Element => {
  const [viewSelected, setView] = useState<string>(view);
  const { onFormatLocale } = useContext(LocResourcesContext);
  const { isLogged } = useContext(CartEventsContext);
  const [isPreviousRewardsChecked, setIsPreviousRewardsChecked] = useState<boolean>(false);
  const [isRedeemableRewardsChecked, setIsRedeemableRewardsChecked] = useState<boolean>(false);
  const {
    showFilters,
    onShowFilters,
    selectedPetTypes,
    selectedProductTypes,
    onSetPreviousPurchased,
    previousPurchased,
    onSetRedeemableRewards,
    redeemableRewards,
    onClearFilters,
    articlesFiltered,
    anyFilterApplied,
  } = useContext(FiltersContext);

  useEffect(() => {
    setIsPreviousRewardsChecked(previousPurchased ?? false);
    setIsRedeemableRewardsChecked(redeemableRewards ?? false);
  }, [previousPurchased, redeemableRewards]);

  const handlePreviousRewardsToggle = () => {
    setIsPreviousRewardsChecked(!isPreviousRewardsChecked);
    onSetPreviousPurchased?.(!isPreviousRewardsChecked);
  };

  const handleRedeemableRewardsToggle = () => {
    setIsRedeemableRewardsChecked(!isRedeemableRewardsChecked);
    onSetRedeemableRewards?.(!isRedeemableRewardsChecked);
  };

  const handleCloseFilters = () => {
    onShowFilters?.(false);
  };

  return (
    <Drawer
      closeBtnAriaLabel={
        onFormatLocale ? onFormatLocale?.('loy.rewards.shop.filter.closeButton') : ''
      }
      id="filters-popup"
      isOpen={showFilters}
      onClose={() => onShowFilters?.(false)}
      title="Filters">
      {viewSelected === 'all' && (
        <div className="filtersContent">
          <ul className="filterCategoryList">
            <li className="filterCategory">
              <FilterCategoryButton
                view="PetTypes"
                selectedCategoryTypes={selectedPetTypes}
                setView={setView}
                buttonText="loy.rewards.shop.filter.type.pet"
                numberBadgeAriaLabel="loy.rewards.shop.filter.selectedPetTypes"
              />
            </li>
            <li className="filterCategory">
              <FilterCategoryButton
                view="ProductTypes"
                selectedCategoryTypes={selectedProductTypes}
                setView={setView}
                buttonText="loy.rewards.shop.filter.type.product"
                numberBadgeAriaLabel="loy.rewards.shop.filter.selectedProductTypes"
              />
            </li>
            <li className="filterCategory">
              <ZooPointsSlider />
            </li>
            {isLogged && (
              <>
                <li className="filterCategory">
                  <div className="switchContainer">
                    <label htmlFor="previous-rewards-switch">
                      {onFormatLocale?.('mzp.bp.product.filter.previousrewards')}
                    </label>
                    <Switch
                      id="previous-rewards-switch"
                      isPressed={isPreviousRewardsChecked}
                      onClick={handlePreviousRewardsToggle}
                    />
                  </div>
                </li>
                <li className="filterCategory">
                  <div className="switchContainer">
                    <label htmlFor="redeemable-rewards-switch">
                      {onFormatLocale?.('loy.rewards.shop.filter.redeemablerewards')}
                    </label>
                    <Switch
                      id="redeemable-rewards-switch"
                      isPressed={isRedeemableRewardsChecked}
                      onClick={handleRedeemableRewardsToggle}
                    />
                  </div>
                </li>
              </>
            )}
          </ul>
          <div className="buttonsContainer">
            <SecondaryButton
              length="full"
              onClick={onClearFilters}
              className={`drawerButton ${!anyFilterApplied ? 'hideButton' : ''}`}>
              {onFormatLocale?.('loy.rewards.shop.filter.clearFiltersButton')}
            </SecondaryButton>
            <PrimaryButton length="full" className="drawerButton" onClick={handleCloseFilters}>
              {anyFilterApplied
                ? onFormatLocale?.('loy.rewards.shop.filter.shownumberofrewards', {
                    quantity: articlesFiltered?.length,
                  })
                : onFormatLocale?.('loy.rewards.shop.filter.showrewards')}
            </PrimaryButton>
          </div>
        </div>
      )}
      {viewSelected === 'PetTypes' && (
        <ListSelector setView={setView} view="PetTypes" title="loy.rewards.shop.filter.type.pet" />
      )}
      {viewSelected === 'ProductTypes' && (
        <ListSelector
          setView={setView}
          view="ProductTypes"
          title="loy.rewards.shop.filter.type.product"
        />
      )}
    </Drawer>
  );
};
